import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { canAccessRoute, getDefaultRoute } from '../../utils/permissions';

// Paths that are accessible to any authenticated user regardless of role.
// These are the "shared" application pages that don't have a specific
// ROUTE_ACCESS entry in permissions.js.
const UNIVERSAL_PATHS = new Set(['/', '/notifications', '/profile']);

export default function ProtectedRoute({ allowedRoles }) {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking auth state
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress sx={{ color: '#1B365D' }} />
      </Box>
    );
  }

  // Not authenticated → redirect to login (preserving the attempted URL)
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check optional explicit role whitelist (prop-based)
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={getDefaultRoute(user.role)} replace />;
  }

  // Enforce per-path role access from permissions.js — this prevents a
  // user from typing another role's URL (e.g. a free user entering
  // /admin/users). Universal paths skip the check.
  const path = location.pathname;
  if (!UNIVERSAL_PATHS.has(path) && !canAccessRoute(user.role, path)) {
    return <Navigate to={getDefaultRoute(user.role)} replace />;
  }

  return <Outlet />;
}
